import { GridTee } from "@/components/ui/grid-connector";
import { cn } from "@/lib/utils";

/** Horizontal page gutter — Figma px-80 on 1440 canvas. */
export const GROWING_GUTTER = "px-6 lg:px-20";

interface GrowingPanelProps {
  children: React.ReactNode;
  className?: string;
  /** Show grid-connector marks at the top corners of the white panel. */
  connectors?: boolean;
  /** Collapse the top border with the previous white panel (Figma stacked containers). */
  stack?: boolean;
  /**
   * Figma mobile frames (e.g. 9644:35441) stack cards on grey-50 without the
   * desktop white bordered container.
   */
  bareMobile?: boolean;
  /** Stable selector for visual-verify section screenshots. */
  "data-section"?: string;
}

/** White bordered panel on grey-50 — Figma Container (1280×, p-64). Sections stack flush. */
export function GrowingPanel({
  children,
  className,
  connectors = false,
  stack = true,
  bareMobile = false,
  "data-section": dataSection,
}: GrowingPanelProps) {
  return (
    <section
      className={cn("bg-grey-50 overflow-x-clip", GROWING_GUTTER, className)}
      data-section={dataSection}
      data-testid={dataSection ? `${dataSection}-section` : undefined}
    >
      <div
        className={cn(
          "border-grey-100 bg-grey-0 relative mx-auto max-w-[1280px] border px-6 py-12 md:p-16",
          stack && "-mt-px",
          bareMobile &&
            "max-lg:mx-0 max-lg:max-w-none max-lg:border-0 max-lg:bg-transparent max-lg:px-0 max-lg:py-0"
        )}
      >
        {connectors && (
          <>
            {/*
             * Figma 8301:72989 / 8301:72988 — 7×4 T on the top border, stem down
             * into the panel (first unstacked white container on the page grid).
             */}
            <GridTee className="text-grey-950 pointer-events-none absolute -top-[0.5px] -left-[3.5px] z-10 hidden lg:block" />
            <GridTee className="text-grey-950 pointer-events-none absolute -top-[0.5px] -right-[3.5px] z-10 hidden lg:block" />
          </>
        )}
        {children}
      </div>
    </section>
  );
}

/** Full-bleed grey band with page gutter — for video / customers (Figma p-80). */
export function GrowingBand({
  children,
  className,
  "data-section": dataSection,
}: {
  children: React.ReactNode;
  className?: string;
  "data-section"?: string;
}) {
  return (
    <section
      className={cn("bg-grey-50 p-6 lg:p-20", className)}
      data-section={dataSection}
      data-testid={dataSection ? `${dataSection}-section` : undefined}
    >
      <div className="mx-auto max-w-[1280px]">{children}</div>
    </section>
  );
}
